/**
 * Volume-render presets — window/level + colour ramps for common CT
 * and MR tissue classes. Hand-tuned against the published TSV + HU
 * bands used by RadiAnt / 3D Slicer / Horos.
 *
 * Keep the list short: a radiologist cycles through these with number
 * keys, not via a drop-down; every entry that isn't immediately useful
 * is friction.
 */
import type { VolumePreset } from "./types";

/** Standard CT / MR window-level bands used by the radiology community.
 *  Values align with RSNA/Radiopaedia and match the 2-D preset table
 *  in `src/protocols/presets.ts` — one source of truth so the same
 *  tissue class looks the same whether a reader is in 2-D or 3-D. */
export const CT_PRESETS: VolumePreset[] = [
  // ---------- Bone ----------
  {
    name: "Bone (MIP)",
    wc: 500, ww: 2000, mode: "mip",
  },
  {
    name: "Bone (DVR)",
    wc: 400, ww: 1500, mode: "dvr",
    colormap: [
      { pos: 0.0, color: "#000000" },
      { pos: 0.3, color: "#5c3a22" },
      { pos: 0.55, color: "#c89a6b" },
      { pos: 0.85, color: "#f5e6cc" },
      { pos: 1.0, color: "#ffffff" },
    ],
  },

  // ---------- Brain ----------
  {
    // Standard head CT band — grey / white differentiation.
    name: "Brain (DVR)",
    wc: 40, ww: 80, mode: "dvr",
    colormap: [
      { pos: 0.0,  color: "#000000" },
      { pos: 0.25, color: "#261414" },
      { pos: 0.50, color: "#7a5c50" },
      { pos: 0.80, color: "#d9c1a3" },
      { pos: 1.0,  color: "#fff4e0" },
    ],
  },
  {
    // Thin MIP of brain parenchyma — useful for perfusion previews.
    name: "Brain (MIP)",
    wc: 40, ww: 80, mode: "mip",
  },

  // ---------- Thorax / lung ----------
  {
    name: "Lung (MIP)",
    wc: -600, ww: 1500, mode: "mip",
  },
  {
    // Minimum-intensity projection highlights airways (low HU).
    name: "Airway (MinIP)",
    wc: -400, ww: 1000, mode: "minip",
  },
  {
    name: "Mediastinum (DVR)",
    wc: 50, ww: 350, mode: "dvr",
    colormap: [
      { pos: 0.0,  color: "#000000" },
      { pos: 0.25, color: "#2b1a10" },
      { pos: 0.55, color: "#8c5a3a" },
      { pos: 0.85, color: "#d9b089" },
      { pos: 1.0,  color: "#f5dfc2" },
    ],
  },

  // ---------- Angio / vascular ----------
  {
    name: "Angio (MIP)",
    wc: 200, ww: 700, mode: "mip",
  },
  {
    name: "Angio (DVR)",
    wc: 170, ww: 400, mode: "dvr",
    colormap: [
      { pos: 0.0, color: "#000000" },
      { pos: 0.35, color: "#7a1111" },
      { pos: 0.70, color: "#e53935" },
      { pos: 1.0, color: "#fff176" },
    ],
  },

  // ---------- Abdomen / soft tissue ----------
  {
    // Standard abdominal window. Same HU band as the 2-D "Abdomen"
    // preset.
    name: "Abdomen (DVR)",
    wc: 40, ww: 400, mode: "dvr",
    colormap: [
      { pos: 0.0,  color: "#000000" },
      { pos: 0.25, color: "#3b2414" },
      { pos: 0.55, color: "#b05c2a" },
      { pos: 0.80, color: "#e6a868" },
      { pos: 1.0,  color: "#faeacc" },
    ],
  },
  {
    name: "Soft tissue",
    wc: 50, ww: 400, mode: "dvr",
    colormap: [
      { pos: 0.0, color: "#000000" },
      { pos: 0.25, color: "#3b2414" },
      { pos: 0.55, color: "#d38a67" },
      { pos: 1.0, color: "#f6e0d2" },
    ],
  },
  {
    name: "Liver (DVR)",
    wc: 90, ww: 200, mode: "dvr",
    colormap: [
      { pos: 0.0, color: "#000000" },
      { pos: 0.20, color: "#4c1e12" },
      { pos: 0.50, color: "#b05c2a" },
      { pos: 0.80, color: "#e8a76a" },
      { pos: 1.0, color: "#fbe4c2" },
    ],
  },

  // ---------- Full range ----------
  {
    // Everything from air to bone in one pass — useful when the reader
    // isn't sure what tissue they're looking at yet.
    name: "Full range (MIP)",
    wc: 300, ww: 3000, mode: "mip",
  },
];

export function presetByName(name: string): VolumePreset | undefined {
  return CT_PRESETS.find((p) => p.name === name);
}

export function defaultPreset(): VolumePreset {
  return CT_PRESETS[0]; // Bone MIP — shows "there's a skeleton" on first open
}

/** Turn a colormap's colour stops into a 256-entry RGBA Uint8 array
 *  suitable for upload as a 1-D LUT texture. Linear sRGB interpolation
 *  between stops; alpha is derived from the intensity so empty-air
 *  (pos 0) is fully transparent and tissue (pos 1) is opaque. */
export function buildColormapLUT(
  colormap?: Array<{ pos: number; color: string }>,
): Uint8Array {
  const lut = new Uint8Array(256 * 4);
  const stops = colormap && colormap.length >= 2 ? colormap : [
    { pos: 0, color: "#000000" },
    { pos: 1, color: "#ffffff" },
  ];
  const sorted = [...stops].sort((a, b) => a.pos - b.pos);

  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let a = sorted[0], b = sorted[sorted.length - 1];
    for (let k = 0; k < sorted.length - 1; k++) {
      if (t >= sorted[k].pos && t <= sorted[k + 1].pos) {
        a = sorted[k]; b = sorted[k + 1]; break;
      }
    }
    const span = Math.max(1e-6, b.pos - a.pos);
    const local = Math.max(0, Math.min(1, (t - a.pos) / span));
    const [ar, ag, ab] = hex(a.color);
    const [br, bg, bb] = hex(b.color);
    const r = Math.round(ar + (br - ar) * local);
    const g = Math.round(ag + (bg - ag) * local);
    const b2 = Math.round(ab + (bb - ab) * local);
    lut[i * 4 + 0] = r;
    lut[i * 4 + 1] = g;
    lut[i * 4 + 2] = b2;
    // Alpha ramp — empty air transparent, bright tissue opaque.
    // Power curve keeps soft tissue soft without washing out bones.
    lut[i * 4 + 3] = Math.round(Math.pow(t, 2.0) * 255);
  }
  return lut;
}

function hex(h: string): [number, number, number] {
  const s = h.replace("#", "");
  const n = parseInt(s.length === 3
    ? s.split("").map((c) => c + c).join("")
    : s, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
